import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  getConsent,
  setConsent,
  CONSENT_REOPEN_EVENT,
} from "@/lib/cookieConsent";

/**
 * DPDP cookie-consent banner. Shows on first visit (or after a consent-version
 * bump) with Accept all / Reject non-essential / Manage preferences. The
 * decision is opt-in: nothing non-essential runs until the visitor accepts.
 * Re-openable via the CONSENT_REOPEN_EVENT (footer "Cookie preferences" link).
 */
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(() => getConsent() == null);
  const [managing, setManaging] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(() => getConsent()?.analytics ?? false);

  useEffect(() => {
    const reopen = () => {
      setAnalyticsOn(getConsent()?.analytics ?? false);
      setManaging(true);
      setVisible(true);
    };
    window.addEventListener(CONSENT_REOPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, reopen);
  }, []);

  if (!visible) return null;

  const decide = (analytics: boolean) => {
    setConsent(analytics);
    setVisible(false);
    setManaging(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 inset-x-0 z-[100] p-3 sm:p-4"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
              <Cookie className="w-[18px] h-[18px] text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">We value your privacy</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                We use essential storage to keep you signed in, and optional analytics
                (anonymised view/click counts) to improve search and recommendations.
                You can change this any time from the footer.{" "}
                <Link to="/privacy" className="text-indigo-600 hover:text-indigo-700 font-semibold underline-offset-2 hover:underline">
                  Privacy policy
                </Link>
              </p>
            </div>
            {managing && (
              <button
                type="button"
                aria-label="Close preferences"
                onClick={() => { setVisible(getConsent() == null); setManaging(false); }}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {managing && (
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-slate-800">Essential</p>
                  <p className="text-xs text-slate-500">Sign-in session and security. Always on.</p>
                </div>
                <Switch checked disabled aria-label="Essential storage (always on)" />
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-slate-800">Analytics</p>
                  <p className="text-xs text-slate-500">Anonymised view/click counts that power popularity ranking.</p>
                </div>
                <Switch
                  checked={analyticsOn}
                  onCheckedChange={setAnalyticsOn}
                  aria-label="Allow analytics"
                />
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
            {!managing ? (
              <>
                <button
                  type="button"
                  onClick={() => setManaging(true)}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-3 py-2 transition-colors"
                >
                  Manage preferences
                </button>
                <Button variant="outline" size="sm" onClick={() => decide(false)}>
                  Reject non-essential
                </Button>
                <Button size="sm" onClick={() => decide(true)}>
                  Accept all
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => decide(analyticsOn)}>
                Save preferences
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
